import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { LargeShareholderSearchFilter } from '../models/largeShareholderSearchFilter';
import { PaginatedResponse } from '../models/paginatedResponse';
import { BulkMergeRequest } from '../models/bulkMergeRequest';
import { LargeShareholderDto } from '../models/api/largeShareholderDto';
import { PyramidDto } from '../models/api/pyramidDto';
import { LargeShareholderDetailDto } from '../models/api/largeShareholderDetailDto';
import { CreateLargeShareholderRequest } from '../models/api/createLargeShareholderRequest';
import { UpdateLargeShareholderRequest } from '../models/api/updateLargeShareholderRequest';
import { DeletePyramidRequest } from '../models/api/deletePyramidRequest';
import { DeletePyramidYearsRequest } from '../models/api/deletePyramidYearsRequest';
import { UpsertMultiYearPyramidRequest } from '../models/api/upsertMultiYearPyramidRequest';
import { LargeShareholderConnectionDto } from '../models/api/largeShareholderConnectionDto';
import { CreateConnectionRequest } from '../models/api/createConnectionRequest';
import { SearchPyramidEntitiesRequest } from '../models/api/searchPyramidEntitiesRequest';
import { PyramidEntityDto } from '../models/api/pyramidEntityDto';
import { environment } from '../../environments/environment';
import { PyramidEntriesByYearDto } from '../models/api/pyramidEntriesByYearDto';
import { LargeShareholderHoldingsResponseDto } from '../models/api/largeShareholderHoldingsDto';

@Injectable({
  providedIn: 'root'
})
export class LargeShareholderService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/large-shareholders`;

  // ==================== GET Operations ====================

  /**
   * Legacy method for backwards compatibility - use searchLargeShareholders instead
   * @deprecated Use searchLargeShareholders for paginated results
   */
  getLargeShareholders(searchFilter: LargeShareholderSearchFilter): Observable<PaginatedResponse<LargeShareholderDto>> {
    return this.searchLargeShareholders({
      ...searchFilter,
      pageNumber: searchFilter.pageNumber || 1,
      pageSize: searchFilter.pageSize || 100,
      sortBy: searchFilter.sortBy || 'name',
      ascending: searchFilter.ascending !== undefined ? searchFilter.ascending : true
    });
  }

  /**
   * Get a large shareholder by ID with optional year parameter
   */
  getLargeShareholderById(id: string, year?: number): Observable<LargeShareholderDetailDto> {
    const params: any = {};
    if (year !== undefined) {
      params.year = year.toString();
    }

    return this.http.get<LargeShareholderDetailDto>(`${this.apiUrl}/${id}`, {
      params
    });
  }

  /**
   * Get count of unconnected large shareholders
   */
  getUnconnectedCount(): Observable<number> {
    return this.http.get<number>(`${this.apiUrl}/unconnected/count`);
  }

  /**
   * Get unconnected large shareholders with pagination
   */
  getUnconnectedLargeShareholders(pageNumber: number = 1, pageSize: number = 50): Observable<PaginatedResponse<LargeShareholderDto>> {
    return this.http.get<PaginatedResponse<LargeShareholderDto>>(`${this.apiUrl}/unconnected`, {
      params: {
        pageNumber: pageNumber.toString(),
        pageSize: pageSize.toString()
      }
    });
  }

  /**
   * Get all connections for a large shareholder
   */
  getLargeShareholderConnections(largeShareholderId: string): Observable<LargeShareholderConnectionDto[]> {
    return this.http.get<LargeShareholderConnectionDto[]>(`${this.apiUrl}/${largeShareholderId}/connections`);
  }

  /**
   * Get all pyramid entries for a large shareholder, grouped by year.
   * Entries that span multiple years are broken down into individual year entries.
   */
  getPyramidEntriesByYear(largeShareholderId: string): Observable<PyramidEntriesByYearDto> {
    return this.http.get<PyramidEntriesByYearDto>(`${this.apiUrl}/${largeShareholderId}/pyramid`);
  }

  /**
   * Get all holdings for a large shareholder across all years and companies
   */
  getHoldings(id: string): Observable<LargeShareholderHoldingsResponseDto> {
    return this.http.get<LargeShareholderHoldingsResponseDto>(`${this.apiUrl}/${id}/holdings`);
  }

  // ==================== POST Operations ====================

  /**
   * Search for large shareholders with pagination and filtering
   */
  searchLargeShareholders(request: LargeShareholderSearchFilter): Observable<PaginatedResponse<LargeShareholderDto>> {
    return this.http.post<PaginatedResponse<LargeShareholderDto>>(`${this.apiUrl}/search`, request);
  }

  /**
   * Export large shareholders to CSV based on search criteria
   */
  exportLargeShareholdersToCsv(request: LargeShareholderSearchFilter): Observable<Blob> {
    return this.http.post(`${this.apiUrl}/search/export`, request, {
      responseType: 'blob'
    });
  }

  /**
   * Create a new large shareholder
   */
  createLargeShareholder(request: CreateLargeShareholderRequest): Observable<LargeShareholderDetailDto> {
    return this.http.post<LargeShareholderDetailDto>(this.apiUrl, request);
  }

  /**
   * Merge two large shareholders (leftId is kept, rightId is merged into it)
   */
  mergeLargeShareholders(leftId: string, rightId: string): Observable<void> {
    const params = new HttpParams()
      .set('leftId', leftId)
      .set('rightId', rightId);

    return this.http.post<void>(`${this.apiUrl}/merge`, null, { params });
  }

  /**
   * Bulk merge multiple large shareholders into one
   */
  bulkMergeLargeShareholders(request: BulkMergeRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/merge/bulk`, request);
  }

  /**
   * Manage large shareholder pyramid entries for multiple years.
   * Creates new entries if they don't exist, or updates existing entries (upsert).
   */
  upsertMultiYearPyramid(request: UpsertMultiYearPyramidRequest): Observable<PyramidDto[]> {
    return this.http.post<PyramidDto[]>(`${this.apiUrl}/pyramid`, request);
  }

  /**
   * Search for entities that can be used in pyramid relationships
   */
  searchPyramidEntities(request: SearchPyramidEntitiesRequest): Observable<PyramidEntityDto[]> {
    return this.http.post<PyramidEntityDto[]>(`${this.apiUrl}/pyramid/search-entities`, request);
  }

  /**
   * Create a new connection for a large shareholder
   */
  createConnection(request: CreateConnectionRequest): Observable<LargeShareholderConnectionDto> {
    return this.http.post<LargeShareholderConnectionDto>(`${this.apiUrl}/connections`, request);
  }

  // ==================== PUT Operations ====================

  /**
   * Update an existing large shareholder
   */
  updateLargeShareholder(id: string, request: UpdateLargeShareholderRequest): Observable<LargeShareholderDetailDto> {
    return this.http.put<LargeShareholderDetailDto>(`${this.apiUrl}/${id}`, request);
  }

  // ==================== DELETE Operations ====================

  /**
   * Delete a large shareholder
   */
  deleteLargeShareholder(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /**
   * Delete unconnected large shareholders by IDs
   */
  deleteUnconnectedLargeShareholders(ids: string[]): Observable<string> {
    return this.http.delete(`${this.apiUrl}/unconnected`, {
      body: ids,
      responseType: 'text'
    });
  }

  /**
   * Delete a large shareholder pyramid entry for a specific validity period (year).
   * This removes a year-specific entry from a pyramid relationship.
   */
  deletePyramidEntry(request: DeletePyramidRequest): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/pyramid`, { body: request });
  }

  /**
   * Delete a single pyramid node from a large shareholder's pyramid across several years, atomically.
   * The node is identified by its entity ids (not a row id) so the server can re-resolve the correct
   * historical row per year. Replaces the previous parallel one-request-per-year deletion.
   */
  deletePyramidYears(request: DeletePyramidYearsRequest): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/pyramid/years`, { body: request });
  }

  /**
   * Delete a large shareholder connection
   */
  deleteConnection(connectionId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/connections/${connectionId}`);
  }
}
